import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";

const RAIL_W = 150;
const SIDE_GAP = 46; // distance from the article text — lets the article breathe
const SLOT_H = 166; // approx height of one square slot + gap
const LABEL_H = 28;

const normalizeUrl = (url) => {
  if (!url) return "#";
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const SponsorItem = ({ s }) => (
  <a
    href={normalizeUrl(s.url)}
    target="_blank"
    rel="sponsored nofollow noopener noreferrer"
    title={s.name}
    data-testid={`article-sponsor-${s.id}`}
    className="group block bg-white border border-[#E7E5E4] hover:border-[#9F583F] transition-colors overflow-hidden"
  >
    <div className="w-full aspect-square bg-white flex items-center justify-center p-3">
      {s.logo ? (
        <img
          src={mediaUrl(s.logo)}
          alt={s.name}
          loading="lazy"
          className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <span className="font-display text-center text-sm text-[#1C1917] leading-tight px-1">{s.name}</span>
      )}
    </div>
  </a>
);

const SponsorPlaceholder = () => (
  <Link
    to="/contatti?tipo=sponsor"
    data-testid="article-sponsor-placeholder"
    className="group block border border-dashed border-[#D6CFC4] bg-[#FBFAF8] hover:border-[#9F583F] hover:bg-white transition-colors"
  >
    <div className="w-full aspect-square flex flex-col items-center justify-center p-3 text-center gap-1">
      <span className="label-ui text-[#A8A29E] group-hover:text-[#9F583F] transition-colors leading-tight">
        Il tuo logo qui
      </span>
    </div>
  </Link>
);

// Vertical sponsor rails on both sides of an article — desktop only.
// Fills down to the end of the article with "Il tuo logo qui" placeholders,
// and rotates the real sponsors on each visit to give everyone visibility.
export const ArticleSponsors = () => {
  const [sponsors, setSponsors] = useState([]);
  const [perSide, setPerSide] = useState(0);
  const leftRef = useRef(null);

  useEffect(() => {
    api.get("/sponsors").then((r) => setSponsors(r.data || [])).catch(() => {});
  }, []);

  // shuffle once per mount → rotation across visits
  const shuffled = useMemo(() => {
    const arr = [...sponsors];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [sponsors]);

  // measure the article height to know how many slots fit per side
  useEffect(() => {
    const el = leftRef.current;
    if (!el) return;
    const parent = el.offsetParent; // the <article> (position: relative)
    if (!parent) return;
    const compute = () => {
      const usable = parent.clientHeight - LABEL_H;
      const n = Math.max(0, Math.floor(usable / SLOT_H));
      setPerSide(n);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(parent);
    return () => ro.disconnect();
  }, [shuffled]);

  if (sponsors.length === 0) return null;

  const slots = Math.max(perSide, Math.ceil(shuffled.length / 2));
  const leftItems = [];
  const rightItems = [];
  for (let i = 0; i < slots; i++) {
    leftItems.push(shuffled[2 * i] || null);
    rightItems.push(shuffled[2 * i + 1] || null);
  }

  const Rail = ({ items, side, innerRef }) => (
    <div
      ref={innerRef}
      className="hidden xl:flex flex-col gap-4 absolute top-2"
      style={{ width: RAIL_W, ...(side === "left" ? { right: "100%", marginRight: SIDE_GAP } : { left: "100%", marginLeft: SIDE_GAP }) }}
      data-testid={`article-sponsor-rail-${side}`}
      aria-label="Sponsor"
    >
      <span className="label-ui text-[#A8A29E] text-center">Sponsor</span>
      {items.map((s, i) => (s ? <SponsorItem key={s.id} s={s} /> : <SponsorPlaceholder key={`ph-${side}-${i}`} />))}
    </div>
  );

  return (
    <>
      <Rail items={leftItems} side="left" innerRef={leftRef} />
      <Rail items={rightItems} side="right" />
    </>
  );
};
